import {
  RATING_CATEGORY_LESSON,
  saveUserRating,
  getUserRatings,
  updateUserRating
} from 'components/common/api/Ratings'
import { useContext, useState, useEffect, useRef } from 'react'
import CourseContext from 'components/common/contexts/CourseContext'
import { Button, Form } from 'react-bootstrap'
import { navigateTo } from '../../common/navigation'
import { getCoursePath } from '../../common/helpers/Paths'
import { useHistory, useParams } from 'react-router-dom'

/**
 * Confirmation component. Used for displaying a confirmation message and directing a user to a link.
 * @param {Object} params Initial props:
 * `title` - Title of the confirmation
 * `subtitle` - Subtitle of the confirmation
 * `message` - Message of the confirmation
 * `btnOnClick` - Function to execute when the button is clicked
 * `btnText` - Text of the button
 * `linkOnClick` - Function to execute when the link is clicked
 * `linkText` - Text of the link
 * `showConfetti` - Whether to show confetti (default: false)
 */
const pleaseRate = 'Was this session helpful?'

const thumbsUpRate =
  'We’re glad to hear! Please tell us what you liked most about the session and anything you would like to add.'

const thumbsDownRate =
  'We’re sorry to hear. Please tell us why you did not find the session helpful and how it could have been a better experience.'

export default function CourseRating({ lesson, buttonProps }) {
  let confirmationTextContainer = useRef(null)
  const history = useHistory()
  const params = useParams()
  const ref = useRef()
  const { courseId } = params
  const { btnText, btnOnClick } = buttonProps
  const { ratings, setRatings } = useContext(CourseContext)

  const [ratingCreated, setRatingCreated] = useState()
  const [userRatedDate, setUserRatedDate] = useState()
  const [showCommentContainer, setShowCommentContainer] = useState(false)
  const [userRating, setUserRating] = useState(null)
  const [userComment, setUserComment] = useState('')

  const hasRating = rating =>
    rating.category_object_id === lesson?.id &&
    rating.category === RATING_CATEGORY_LESSON

  const thumbsClickActions = rate => {
    setRatingCreated(rate)
    setShowCommentContainer(true)
    confirmationTextContainer.current.classList.add('d-none')
  }

  const handleThumbsClick = async (rating, ratingText) => {
    let saveResult
    if (ratings?.some(hasRating)) {
      const userRating = ratings.find(hasRating)
      await updateUserRating({
        ...userRating,
        id: userRating.id,
        rating: rating,
        comment: userRating.comment
      })
    } else {
      saveResult = await saveUserRating({
        category_object_id: lesson?.id,
        category: RATING_CATEGORY_LESSON,
        rating: rating
      })
      setRatings(ratings.concat([saveResult]))
    }
    thumbsClickActions(ratingText)
  }

  const handleThumbsClassName = (rating, ratingText) =>
    `bi bi-hand-thumbs-${ratingText} ${
      userRating && userRating[0]?.rating === rating ? 'active' : ''
    }`

  const handleSaveRating = async () => {
    if (userRating.length > 0) {
      const updatedData = {
        id: userRating[0].id,
        rating: userRating[0].rating,
        comment: ref.current.value
      }

      // update user rating data
      await updateUserRating(updatedData)

      const updatedRatings = ratings.map(rating => {
        if (rating.id === userRating[0].id) {
          return {
            ...rating,
            ...updatedData
          }
        } else return rating
      })

      setRatings(updatedRatings)
    }
    navigateTo(getCoursePath(courseId), history)
  }

  useEffect(() => {
    confirmationTextContainer.current =
      document.querySelector('.confirmation-text')
  }, [confirmationTextContainer])

  useEffect(() => {
    async function getRatingCreatedAsync() {
      const lessonRating = await getUserRatings({
        category: RATING_CATEGORY_LESSON,
        category_object_id: lesson?.id
      })

      const date = new Date(lessonRating[0]?.created_on)
      const splittedDate = date.toDateString().split(' ')
      const formattedDate = `${splittedDate[1]} ${splittedDate[2]}, ${splittedDate[3]}`

      const updateRate =
        lessonRating[0] &&
        `You rated this thumbs ${
          lessonRating[0]?.rating === 1 ? 'up' : 'down'
        } on ` + formattedDate

      setUserRating(lessonRating)
      setUserRatedDate(updateRate)
    }

    getRatingCreatedAsync()
  }, [userRatedDate, ratings, lesson])

  useEffect(() => {
    if (userRating?.length > 0) {
      userRating[0].comment && setUserComment(userRating[0].comment)
    }
  }, [userRating])

  if (!lesson) return

  return (
    <>
      {!showCommentContainer && (
        <>
          <div className='rate mb-2'>{pleaseRate}</div>
          <div className='d-flex justify-content-center align-items-center gap-3'>
            <i
              onClick={() => handleThumbsClick(1, thumbsUpRate)}
              className={handleThumbsClassName(1, 'up')}
            ></i>
            <i
              onClick={() => handleThumbsClick(-1, thumbsDownRate)}
              className={handleThumbsClassName(-1, 'down')}
            ></i>
          </div>
          <div className='muted mt-2 mt-md-4'>{userRatedDate}</div>
        </>
      )}

      {showCommentContainer && ratingCreated && (
        <>
          <Form>
            <Form.Label htmlFor='userFeedback'>
              <div className='rate'>{ratingCreated}</div>
            </Form.Label>
            <Form.Group className='my-3' controlId='userFeedback'>
              <Form.Control
                as='textarea'
                rows={3}
                ref={ref}
                value={userComment}
                onChange={e => setUserComment(e.currentTarget.value)}
              />
            </Form.Group>
          </Form>
          <Button
            className='w-100'
            variant='primary'
            onClick={handleSaveRating}
          >
            Save rating
          </Button>
          {btnOnClick && btnText && (
            <Button
              className='mt-4 p-2 text-black'
              variant='link'
              onClick={btnOnClick}
            >
              {btnText}
            </Button>
          )}
        </>
      )}
    </>
  )
}
